/**
 * UpgradePromptModal — inline paywall for a gated action (design Screen 8).
 *
 * Shown when a user attempts a gated action they lack permission for — either
 * from an inline `useEntitlement` gate (the action's onClick opens this) or from
 * the global 403 interceptor in `APIAgent` (a stale-cache miss the backend
 * rejected). Presented as a modal, not a redirect, so the user keeps their
 * context.
 *
 * Reuses the shared `ui/Modal.js` primitive, which already provides the focus
 * trap, ESC-to-close, body scroll lock, `role="dialog"` + `aria-modal`, and a
 * React-portal render. Modal restores focus to the trigger on close via its
 * focus trap. The primary CTA is intentionally last in tab order so users don't
 * accidentally upgrade (design Screen 8 a11y note).
 *
 * Copy adapts to the required plan: Buyer Paperwork (offer paperwork for a home
 * listed elsewhere, part of buying on cutthe6.com — no price quoted) vs Full Service (flat $799 / $1,499 listing fee,
 * seller surface). Prices come from `utils/pricing.js`. The required plan is
 * derived from `requiredPermission` via the static map in `useEntitlement`, or
 * can be passed explicitly as `requiredPlan`.
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the modal is shown.
 * @param {() => void} props.onClose - Dismiss handler ("Maybe later" / ESC / ×).
 * @param {string} [props.requiredPermission] - The gated permission that was hit
 *   (e.g. 'document:generate'); drives the default plan + context copy.
 * @param {('buyer'|'fullservice')} [props.requiredPlan] - Explicit plan override.
 * @param {string} [props.contextMessage] - Human phrase for the gated action
 *   (e.g. "Generating the purchase agreement"); shown in the body headline.
 * @param {() => void} [props.onUpgrade] - Invoked when the user clicks the
 *   primary CTA. In the app shell this navigates to the pricing surface. If
 *   omitted, the CTA navigates to {@link upgradeUrlFor}(requiredPlan).
 */

import React, { useId, useRef } from 'react';
import Modal from '../ui/Modal';
import { planForPermission, upgradeUrlFor } from '../../hooks/useEntitlement';
import {
  LISTING_FEE_CENTS,
  LISTING_FEE_PREMIUM_CENTS,
  LISTING_FEE_PREMIUM_MIN_PRICE,
  formatDollars,
} from '../../utils/pricing';
import styles from './UpgradePromptModal.module.css';

/** Per-plan pitch metadata (design Screen 8). */
const PLAN_PITCH = {
  buyer: {
    title: 'Unlock with Buyer Paperwork',
    priceLine: 'Buyer Paperwork, part of buying on cutthe6.com',
    cta: 'See Buyer Paperwork',
    benefits: [
      'Offer paperwork for a home listed elsewhere',
      'Document e-signing',
      'AI offer suggestions (you decide)',
      'Offers on cutthe6.com homes stay free',
    ],
  },
  fullservice: {
    title: 'Unlock with Full Service',
    // Software-fee framing only (legal §3.1 / README #7) — never "per
    // transaction", "at closing", or "only when you close".
    priceLine: `a flat ${formatDollars(LISTING_FEE_CENTS)} software fee (${formatDollars(LISTING_FEE_PREMIUM_CENTS)} for homes listed at $${LISTING_FEE_PREMIUM_MIN_PRICE.toLocaleString('en-US')} or more) for use of the platform throughout your transaction, paid once when you list your home`,
    cta: 'Get Full Service',
    benefits: [
      'List your home on Cut the 6',
      'AI pricing suggestions',
      'AI negotiation help',
      'Guided closing coordination',
    ],
  },
};

/** Map a context phrase to a sentence-case verb fragment for the headline. */
function buildHeadline(contextMessage, priceLine) {
  const action = contextMessage && contextMessage.trim().length
    ? contextMessage.trim()
    : 'This feature';
  return `${action} needs ${priceLine}.`;
}

export default function UpgradePromptModal({
  open,
  onClose,
  requiredPermission,
  requiredPlan,
  contextMessage,
  onUpgrade,
}) {
  const ctaRef = useRef(null);
  const bodyId = useId();

  const plan = requiredPlan || (requiredPermission ? planForPermission(requiredPermission) : 'buyer');
  const pitch = PLAN_PITCH[plan] || PLAN_PITCH.buyer;
  const headline = buildHeadline(contextMessage, pitch.priceLine);

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade(plan);
      return;
    }
    window.location.assign(upgradeUrlFor(plan));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={pitch.title}
      size="sm"
      describedBy={bodyId}
      footer={
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.secondary}
            onClick={onClose}
            data-testid="upgrade-dismiss"
          >
            Maybe later
          </button>
          <button
            type="button"
            className={styles.primary}
            onClick={handleUpgrade}
            ref={ctaRef}
            data-testid="upgrade-cta"
          >
            {pitch.cta}
          </button>
        </div>
      }
    >
      <div id={bodyId} data-testid="upgrade-prompt">
        <p className={styles.headline}>{headline}</p>
        <ul className={styles.benefits}>
          {pitch.benefits.map((benefit) => (
            <li key={benefit} className={styles.benefit}>
              <span className={styles.check} aria-hidden="true">✓</span>
              <span>{benefit}</span>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
